'use client';

import React, { useEffect, useState } from 'react';
import IComponentProps from '@/interfaces/componentProps'; // Restored original interface import
import Image from 'next/image'; // Standard Next.js Image component
import { IconArrowDown } from '@/icons/IconArrowDown';

const ProductSection = ({
  title,
  children,
  className,
  icons,
  id,
  noHorizontalPadding,
}: IComponentProps & { title: string; children: React.ReactNode; icons?: string; id: string }) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [showMoreOrLessVisible, setShowMoreOrLessVisible] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(true);

  // Client-side logic remains the same, relying on document access inside useEffect
  useEffect(() => {
    const element = document.getElementById(id);
    const elementHeight = element?.scrollHeight;

    // 192px is the height limit check
    if (elementHeight && elementHeight > 192) {
      setShowMoreOrLessVisible(true);
    } else {
      setShowMoreOrLessVisible(false);
    }
  }, [id]);

  return (
    <section className={`group relative ${className} ${isOpen ? 'open' : ''}`}>
      {/* Header and Toggle Button */}
      <button
        data-testid={`${id}-toggle-button`}
        onClick={() => setIsOpen(!isOpen)}
        className={`mb-2 flex w-full cursor-pointer items-center text-[1.5rem] font-medium focus:outline-none ${noHorizontalPadding ? 'p-0' : ''}`}
        aria-expanded={isOpen}
        aria-controls={`${id}-content`}
        type="button"
      >
        {icons && (
          <div className="relative mr-2 h-6 w-6 flex-shrink-0 self-center">
            <Image unoptimized={true} fill quality={100} src={icons} alt="icon" />
          </div>
        )}
        <span className="flex-1 text-left">{title}</span>
        <IconArrowDown className={`ml-auto w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Content Area with Max Height and ID for scrollHeight calculation */}
      <div
        id={id} // The div receiving the scrollHeight check
        className={`hidden group-[.open]:block ${!showMore ? 'h-auto' : 'max-h-64 overflow-hidden'} ${noHorizontalPadding ? '' : 'pl-2.5'}`}
      >
        {children}
      </div>

      {/* Read More/Less Toggle */}
      {showMoreOrLessVisible && (
        <button
          data-testid={`${id}-read-more-button`}
          className={`${showMore ? 'absolute' : ''} inset-x-0 bottom-0 hidden cursor-pointer bg-white text-center underline group-[.open]:block ${
            showMore ? 'show-more-less pt-12' : 'pt-4'
          }`}
          onClick={() => setShowMore(!showMore)}
          aria-expanded={showMore}
          aria-controls={`${id}-content`}
          type="button"
        >
          {showMore ? 'Read More' : 'Read Less'}
        </button>
      )}

      {/* Custom styles converted from <style jsx> to a standard <style> tag */}
      <style>{`
        .show-more-less {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 20%, rgba(255, 255, 255, 1) 75%);
        }
      `}</style>
    </section>
  );
};

export default ProductSection;
